import { AssemblyFlexibleProps, COMPONENT_TYPE_ENUM, FAQ_TYPE_ENUM } from './models';

import {
  AssemblyLevel0TopCarouselItemsCollectionArticleFragment,
  GetBottomContentSectionQuery,
  GetFlexibleAssemblyQuery,
  GetTopContentSectionQuery,
  Level0CarouselTeaserFieldsFragment,
  TeaserFieldsFragment,
  TeaserWrapperTeaserListCollection,
} from 'graphql/__generated/sdk';
import { SectionItem } from 'pages/CodeOfConduct/models';
import { URL_LANGUAGE_ENUM } from 'utils/constants';
import { generateContentBlocks, generateEntrySlug } from 'utils/mappers';
import { BaseCardContent } from 'utils/models';
import { renderRichText } from 'utils/rich-text-render';
import {
  generateBaseCard,
  generateHeroBanner,
  generateSeoMetadata,
  getFirstContentItem,
} from 'utils/transformers';

interface SimpleUspEntity {
  __typename: 'UspEntity';
  title: string;
  description: string;
  icon: string;
}

type FlexibleTopSection =
  GetTopContentSectionQuery['data']['items'][0]['topContentSectionCollection']['items'];

const generateUspsList = (icons: SimpleUspEntity[]) => {
  const iconList = icons;
  if (iconList && iconList.length) {
    return iconList.map((icon: SimpleUspEntity) => ({
      iconName: icon.icon?.toLowerCase() ?? 'error',
      title: icon.title,
      description: icon.description,
    }));
  }
  return [];
};

function buildBaseCard(
  card:
    | AssemblyLevel0TopCarouselItemsCollectionArticleFragment
    | Level0CarouselTeaserFieldsFragment,
  language: URL_LANGUAGE_ENUM
): BaseCardContent {
  if (card.__typename === 'Article') {
    const url = generateEntrySlug(card, language);

    return generateBaseCard({
      title: card.title,
      url,
      image: getFirstContentItem(card.images)!,
      body: card.preamble || '',
      category: card.category || '',
    });
  }

  return generateBaseCard({
    title: card.title,
    url: card.teaserUrl || '',
    image: card.primaryMediaImage,
    body: renderRichText(card.richPreamble?.json) || '',
    tagline: card.tagline || '',
  });
}

function prepareBaseCards(
  topCarouselItemsCollection: {
    items:
      | TeaserFieldsFragment[]
      | AssemblyLevel0TopCarouselItemsCollectionArticleFragment[]
      | TeaserWrapperTeaserListCollection['items'];
  },
  language: URL_LANGUAGE_ENUM
) {
  return topCarouselItemsCollection.items.map((item) => buildBaseCard(item, language));
}

function prepareMiniParagraphs(content: SectionItem[]) {
  if (content) {
    return content.map((paragraph) => ({
      title: paragraph.title,
      content: renderRichText(paragraph.body.json),
    }));
  }

  return [];
}

function prepareAnchorTabs(anchorTabs: Record<string, string>[]) {
  if (anchorTabs?.length > 0) {
    return anchorTabs.map((tab) => ({
      title: tab.value,
      id: tab.key,
    }));
  }
  return [];
}

const generateContentSection = (
  items: FlexibleTopSection,
  language: URL_LANGUAGE_ENUM,
  richtextSections?: any
) => {
  const content = items.map((item) => {
    if (item.__typename === COMPONENT_TYPE_ENUM.TEASER) {
      return {
        type: COMPONENT_TYPE_ENUM.TEASER,
        variant: item.taglineType,
        ...generateHeroBanner({
          title: item.title,
          preamble: item.richPreamble,
          image: item.primaryMediaImage,
          videoUrl: item.primaryMediaVideo?.video?.url,

          cta: {
            text: item.tagline || '',
            url: item.teaserUrl || '',
          },
        }),
      };
    }
    if (item.__typename === COMPONENT_TYPE_ENUM.USP_ENTITY_WRAPPER) {
      return {
        type: COMPONENT_TYPE_ENUM.USP_ENTITY_WRAPPER,
        title: item.title,
        isTitleCentered: item.titleCentered,
        preamble: item.preamble,
        uspList: item.uspEntriesCollection.items
          ? generateUspsList(item.uspEntriesCollection.items)
          : [],
        backgroundColor: item.backgroundColor ?? null,
        cta: item.cta
          ? {
              text: item.cta.ctaText || '',
              url: item.cta.linkTrigger || '',
            }
          : undefined,
      };
    }
    if (item.__typename === COMPONENT_TYPE_ENUM.TEASER_WRAPPER) {
      if (item.type !== null) {
        return {
          type: COMPONENT_TYPE_ENUM.TEASER_WRAPPER,
          title: item.name ?? null,
          titleCentered: !!item.titleCentered,
          preamble: item.preamble,
          cardType: item.type,
          isCarousel: item.carouselWrapper,
          items: item.teaserListCollection?.items
            ? prepareBaseCards(item.teaserListCollection, language)
            : [],

          backgroundColor: item.backgroundColor ?? null,
          cta: item.cta ?? null,
        };
      }
      if (item.type === null) {
        return {
          type: COMPONENT_TYPE_ENUM.TEASER_WRAPPER,
          title: item.name ?? null,
          titleCentered: !!item.titleCentered,
          preamble: item.preamble,
          cardType: item.type,
          isCarousel: false,
          items: item.teaserListCollection?.items.map((teaser) => {
            return {
              ...generateHeroBanner({
                title: teaser.title,
                preamble: teaser.richPreamble,
                image: teaser.primaryMediaImage,
                videoUrl: teaser.primaryMediaVideo?.video?.url,

                cta: {
                  text: teaser.tagline || '',
                  url: teaser.teaserUrl || '',
                },
              }),
              cardType: teaser.taglineType,
            };
          }),
          backgroundColor: item.backgroundColor ?? null,
          cta: item.cta ?? null,
        };
      }
    }
    if (item.__typename === COMPONENT_TYPE_ENUM.FAQ_SECTION) {
      return {
        type: COMPONENT_TYPE_ENUM.FAQ_SECTION,
        faqType: item.type,
        title: item.sectionTitle,
        items:
          item.type === FAQ_TYPE_ENUM.MINI_PARAGRAPHS
            ? prepareMiniParagraphs(item.faqSectionCollection.items)
            : item.faqSectionCollection.items,
        backgroundColor: item.backgroundColor ?? null,
        cta: item.cta ?? undefined,
      };
    }
    if (item.__typename === COMPONENT_TYPE_ENUM.RICHTEXT_CONTAINER) {
      const section = richtextSections.find(
        (section: any) => section.richtextContainerId === item.richtextContainerId
      );
      return {
        type: COMPONENT_TYPE_ENUM.RICHTEXT_CONTAINER,
        richtextContainerId: item.richtextContainerId,
        disableAnchorTabs: section?.richTextAnchorTabs ? false : true,
        body: {
          content: generateContentBlocks(section.richText?.json),
          links: section.richText?.links,
        },
        reverseOrder: section.reverseOrder ? true : false,
        sideSection:
          section.title && section.description
            ? {
                title: section.title,
                preamble: section.description,
                cta: section.cta,
              }
            : undefined,
      };
    }
    if (item.__typename === COMPONENT_TYPE_ENUM.IMAGE) {
      return {
        type: COMPONENT_TYPE_ENUM.IMAGE,
        url: item.image?.url,
        alt: item.altText,
        focusArea: item.focusArea,
        fullWidth:
          item.contentfulMetadata?.tags && item.contentfulMetadata.tags.length > 0 ? false : true,
      };
    }
  });
  return content;
};

export function getMappedPageContent(
  data: [GetFlexibleAssemblyQuery, GetTopContentSectionQuery, GetBottomContentSectionQuery],
  language: URL_LANGUAGE_ENUM
): AssemblyFlexibleProps {
  const [pageData, topContentData, bottomContentData] = data;
  const content = pageData.data.items[0];
  const topContent = topContentData?.data?.items?.[0]?.topContentSectionCollection?.items || [];
  const bottomContent =
    bottomContentData?.data?.items?.[0]?.topContentSectionCollection?.items || [];
  const contentSection = [...topContent, ...bottomContent];
  const richtextSections = [...(content?.topContentSectionCollection?.items || [])];

  const anchorTabs = prepareAnchorTabs(content?.anchorTabsCollection?.items);

  return {
    heroBanner: content?.heroBanner
      ? generateHeroBanner({
          title: content.heroBanner.title,
          preamble: content.heroBanner.richPreamble,
          image: content.heroBanner.primaryMediaImage,
          videoUrl: content.heroBanner.primaryMediaVideo?.video?.url,
          cta: {
            text: '',
            url: '',
          },
        })
      : undefined,
    showBreadcrumbs: content?.breadcrumbs ? true : false,
    socialMediaShare: content?.socialMediaShare ? true : false,
    anchorTabs: anchorTabs,
    contentSection:
      contentSection.length > 0
        ? generateContentSection(contentSection, language, richtextSections)
        : undefined,
    seoMetadata: generateSeoMetadata(content?.seoMetadata || {}),
  };
}
